import { useState } from 'react';
import { useIntl } from 'react-intl';
import EditablePropertyValue from '~/components/Fields/EditablePropertyValue';
import { ValueText } from '~/components/Fields/LabeledValue';
import { DENSITY_OVERRIDE_RANGE } from '~/domain/patient/roi';
import { ambiguousDecimalMessage } from '~/utils/decimalInput';
import { DENSITY_OVERRIDE_DECIMALS } from '~/utils/units';
import { parseDensityInput } from './densityOverrideInput';

interface DensityOverrideFieldProps {
    /** Current override, or undefined when none is set. */
    value: number | undefined;
    /** Whether editing is allowed (false for approved ROIs). */
    isEditable: boolean;
    /** Commit a new value, or undefined to remove the override. */
    onChange: (value: number | undefined) => void;
    /** Localized aria-label id for the edit affordance. */
    editLabelId: string;
    /**
     * Message id naming the value itself (e.g. "Relative Electron Density") —
     * the accessible name of the text box, so a screen-reader user knows which
     * density they are editing once the pencil is replaced by the editor.
     */
    fieldLabelId: string;
    /** Test id on the value element (preserves existing selectors). */
    dataTestId: string;
}

/**
 * A single density-override value in the structure Properties popover:
 * read-only text by default, with an inline numeric editor (opened via the
 * pencil) when the ROI is editable.
 *
 * Committing an empty field removes the override entirely — the physical
 * property is then omitted from the output RTSTRUCT — while a number sets or
 * updates it (see {@link parseDensityInput}). A value outside the accepted
 * range (`[0, 20]`, see {@link DENSITY_OVERRIDE_RANGE}) is rejected (surfaced,
 * not clamped); a legitimate boundary value such as `0` is preserved and shown,
 * never treated as "unset". The value edits through the shared
 * {@link EditablePropertyValue}, so keyboard behaviour (Enter to accept, Esc
 * to discard, focus back on the pencil) matches the rest of the panel.
 */
export default function DensityOverrideField({
    value,
    isEditable,
    onChange,
    editLabelId,
    fieldLabelId,
    dataTestId,
}: DensityOverrideFieldProps) {
    const intl = useIntl();
    const [isEditing, setIsEditing] = useState(false);
    // Shared by the commit and the validation message so they can never
    // disagree.
    const range = DENSITY_OVERRIDE_RANGE;

    return (
        <EditablePropertyValue
            display={
                value != null ? (
                    <ValueText
                        component="span"
                        fontFamily="fontFamilyMonospace"
                        data-testid={dataTestId}
                    >
                        {value.toFixed(DENSITY_OVERRIDE_DECIMALS)}
                    </ValueText>
                ) : (
                    <ValueText
                        component="span"
                        color="text.secondary"
                        data-testid={dataTestId}
                    >
                        {intl.formatMessage({ id: 'structure.density.notSet' })}
                    </ValueText>
                )
            }
            editable={isEditable}
            editing={isEditing}
            onEditingChange={setIsEditing}
            // Seed with the raw value (no forced trailing zeros); empty when
            // there is no override so the field starts blank.
            seed={value != null ? String(value) : ''}
            onSubmit={(text) => {
                const parsed = parseDensityInput(text, range);
                if (parsed.kind === 'set') {
                    onChange(parsed.value);
                } else if (parsed.kind === 'remove') {
                    onChange(undefined);
                }
            }}
            validate={(text) => {
                const parsed = parseDensityInput(text, range);
                if (parsed.kind === 'ambiguous') {
                    const { id, values } = ambiguousDecimalMessage(
                        text,
                        parsed.readings
                    );
                    return intl.formatMessage({ id }, values);
                }
                return parsed.kind === 'invalid'
                    ? intl.formatMessage(
                          { id: 'structure.density.invalid' },
                          { min: range.min, max: range.max }
                      )
                    : null;
            }}
            editLabel={intl.formatMessage({ id: editLabelId })}
            fieldLabel={intl.formatMessage({ id: fieldLabelId })}
            selectAllOnOpen
            fontWeight={400}
            buttonTestId={`${dataTestId}-edit-button`}
        />
    );
}
